import { useMemo, useState, type FormEvent } from "react";
import { Link, useParams } from "react-router-dom";

import type { ApiError } from "@/api/client";
import { useCategoryDetail } from "@/api/menus";
import { newUuid, useCreateOrder, usePayOrder } from "@/api/orders";
import { payWithTotp } from "@/api/pay_helpers";
import type { FieldItem, ServiceItem } from "@/api/catalog";
import { useT } from "@/lib/i18n";
import { useToasts } from "@/store/toasts";

/**
 * Detail page for one provider_topup category. Renders the imported
 * Giftery fields as a dynamic form, plus a denomination picker. The
 * submit collects all user-entered keys (account_number etc.) and
 * sends them to /create_order; the backend defaults quantity=1 for
 * the provider_topup pattern so we never ask the customer for it.
 */

const QTY_LIKE_KEYS = new Set(["quantity", "qty", "amount"]);

function isQtyLikeField(f: FieldItem): boolean {
  return (
    QTY_LIKE_KEYS.has(f.key.toLowerCase()) &&
    (f.type === "int" || f.type === "float")
  );
}

function regexOf(rule: string | null | undefined): RegExp | null {
  // Giftery ships the validation rule as a JSON-encoded string like
  // `{"regex":".*"}`. We only honour the regex member; anything else
  // is enforced server-side.
  if (!rule) return null;
  try {
    const parsed = JSON.parse(rule);
    if (parsed && typeof parsed === "object" && typeof parsed.regex === "string") {
      return new RegExp(parsed.regex);
    }
  } catch {
    // Some rules ship as a bare regex (no JSON wrapper). Try direct.
    try {
      return new RegExp(rule);
    } catch {
      return null;
    }
  }
  return null;
}

export function DirectTopupDetail() {
  const { id } = useParams<{ id: string }>();
  const categoryId = id ? Number(id) : null;
  const cat = useCategoryDetail(categoryId);
  const create = useCreateOrder();
  const pay = usePayOrder();
  const t = useT();
  const push = useToasts((s) => s.push);

  const [selected, setSelected] = useState<number | null>(null);
  const [values, setValues] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  // Hide internal quantity fields (the backend auto-stamps qty=1 for
  // provider_topup). Customer-facing fields are everything else.
  const customerFields = useMemo<FieldItem[]>(() => {
    if (!cat.data) return [];
    return cat.data.fields.filter((f) => !isQtyLikeField(f));
  }, [cat.data]);

  const validate = (): { ok: boolean; errors: Record<string, string> } => {
    const errors: Record<string, string> = {};
    for (const f of customerFields) {
      const v = (values[f.key] ?? "").trim();
      if (!v) {
        if (f.required !== false) errors[f.key] = t("direct_topup.required");
        continue;
      }
      const re = regexOf(f.validationRule);
      if (re && !re.test(v)) {
        errors[f.key] = t("direct_topup.invalid_value");
      }
    }
    return { ok: Object.keys(errors).length === 0, errors };
  };

  const [errors, setErrors] = useState<Record<string, string>>({});
  const selectedSvc: ServiceItem | null = useMemo(() => {
    if (!cat.data || selected === null) return null;
    return cat.data.services.find((s) => s.service_id === selected) ?? null;
  }, [cat.data, selected]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!cat.data || !selectedSvc) return;
    const v = validate();
    setErrors(v.errors);
    if (!v.ok) return;

    setBusy(true);
    const cid = newUuid();
    try {
      await create.mutateAsync({
        service_id: selectedSvc.service_id,
        custom_id: cid,
        fields: customerFields
          .map((f) => ({
            key: f.key,
            value: (values[f.key] ?? "").trim(),
          }))
          .filter((f) => f.value !== ""),
      });
      const result = await payWithTotp(
        (code) =>
          pay.mutateAsync({
            custom_id: cid,
            ...(code ? { totp_code: code } : {}),
          }),
        { title: `Confirm: ${selectedSvc.service_name}` },
      );
      if (result === null) {
        // user cancelled the TOTP prompt
        return;
      }
      const isAsync = result.status === "in_progress";
      push({
        kind:
          result.status === "completed" || isAsync ? "success" : "info",
        title:
          result.status === "completed"
            ? t("direct_topup.completed")
            : isAsync
              ? t("direct_topup.in_progress")
              : result.status === "refunded"
                ? t("direct_topup.refunded")
                : t("direct_topup.insufficient"),
        body:
          result.note ||
          (result.balance ? `Balance: $${result.balance}` : undefined),
        link: {
          to: `/orders/${encodeURIComponent(cid)}`,
          label: t("steam_topup.view_order"),
        },
      });
      if (result.status !== "insufficient") {
        // reset form fields so the next purchase starts clean
        setValues({});
        setSelected(null);
      }
    } catch (err) {
      const e = err as ApiError;
      const detail =
        typeof e.detail === "string"
          ? e.detail
          : e.detail
            ? JSON.stringify(e.detail)
            : "Top-up failed";
      push({ kind: "error", title: "Top-up failed", body: detail });
    } finally {
      setBusy(false);
    }
  };

  if (cat.isLoading)
    return <div className="text-slate-400">{t("common.loading")}</div>;
  if (cat.isError || !cat.data) {
    return (
      <div className="space-y-3">
        <Link to="/direct-topup" className="text-sm text-accent">
          {"<- " + t("direct_topup.back")}
        </Link>
        <div className="text-sm text-slate-500">{t("menu.not_found")}</div>
      </div>
    );
  }

  const c = cat.data;

  // If the category lives under a Direct Top-Up menu, the back link
  // walks one level up (menu -> categories list) instead of jumping
  // straight to the index. Falls back to the index for ungrouped
  // categories.
  const backHref =
    c.menu_id !== null
      ? `/direct-topup/menus/${c.menu_id}`
      : "/direct-topup";
  const backLabel = c.menu_name
    ? `<- ${c.menu_name}`
    : "<- " + t("direct_topup.back");

  return (
    <div className="space-y-6 pb-24">
      <div>
        <Link to={backHref} className="text-sm text-accent">
          {backLabel}
        </Link>
        <div className="mt-3 flex items-center gap-4">
          {c.image_url && (
            <img
              src={c.image_url}
              alt={c.category_name}
              className="h-16 w-16 rounded border border-surface-border object-cover"
            />
          )}
          <h1 className="text-2xl font-semibold text-slate-800">
            {c.category_name}
          </h1>
        </div>
      </div>

      <form onSubmit={submit} className="space-y-6">
        {/* ---- customer-supplied fields (account_number, etc.) ---- */}
        <section className="card space-y-4 p-4 sm:p-6">
          {customerFields.length === 0 ? (
            <div className="text-sm text-slate-500">
              {t("direct_topup.no_fields")}
            </div>
          ) : (
            customerFields.map((f) => (
              <FieldInput
                key={f.key}
                field={f}
                value={values[f.key] ?? ""}
                onChange={(v) =>
                  setValues((cur) => ({ ...cur, [f.key]: v }))
                }
                error={errors[f.key]}
              />
            ))
          )}
        </section>

        {/* ---- denomination picker ---- */}
        <section className="card overflow-hidden">
          <div className="border-b border-surface-border bg-surface-muted px-4 py-2 text-xs uppercase tracking-wide text-slate-500">
            {t("direct_topup.choose_amount")}
          </div>
          <div className="grid gap-2 p-3 sm:grid-cols-2 lg:grid-cols-3">
            {c.services.map((s) => {
              const checked = selected === s.service_id;
              return (
                <button
                  type="button"
                  key={s.service_id}
                  onClick={() => setSelected(s.service_id)}
                  className={
                    "flex items-center justify-between gap-3 rounded border px-3 py-2 text-sm transition " +
                    (checked
                      ? "border-accent bg-accent/10 text-accent"
                      : "border-surface-border hover:border-accent/50")
                  }
                >
                  <span className="truncate text-left">{s.service_name}</span>
                  <span className="font-mono tabular-nums">
                    ${s.price.toFixed(2)}
                  </span>
                </button>
              );
            })}
          </div>
        </section>

        <div className="sticky bottom-0 -mx-4 border-t border-surface-border bg-white px-4 py-3 sm:mx-0 sm:rounded sm:border">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="text-sm">
              {selectedSvc ? (
                <>
                  <span className="text-slate-500">You'll pay</span>{" "}
                  <span className="font-semibold tabular-nums text-slate-800">
                    ${selectedSvc.price.toFixed(2)}
                  </span>
                </>
              ) : (
                <span className="text-slate-400">
                  {t("direct_topup.choose_amount")}
                </span>
              )}
            </div>
            <button
              type="submit"
              disabled={!selectedSvc || busy}
              className="btn btn-primary text-sm"
            >
              {busy ? t("direct_topup.buying") : t("direct_topup.buy")}
              {selectedSvc ? ` · $${selectedSvc.price.toFixed(2)}` : ""}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

function FieldInput({
  field,
  value,
  onChange,
  error,
}: {
  field: FieldItem;
  value: string;
  onChange: (v: string) => void;
  error?: string;
}) {
  const label = field.name || field.key;
  const placeholder = field.description || "";

  if (field.type === "enum" && field.options && field.options.length > 0) {
    const labels = field.option_labels ?? {};
    return (
      <div>
        <label className="label">{label}</label>
        <select
          className="input w-full"
          value={value}
          onChange={(e) => onChange(e.target.value)}
        >
          <option value="">--</option>
          {field.options.map((opt) => (
            <option key={opt} value={opt}>
              {labels[opt] ?? opt}
            </option>
          ))}
        </select>
        {field.description && (
          <div className="mt-1 text-xs text-slate-500">{field.description}</div>
        )}
        {error && <div className="mt-1 text-xs text-rose-600">{error}</div>}
      </div>
    );
  }

  if (field.type === "bool") {
    return (
      <div>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={value === "true"}
            onChange={(e) => onChange(e.target.checked ? "true" : "false")}
          />
          {label}
        </label>
        {error && <div className="mt-1 text-xs text-rose-600">{error}</div>}
      </div>
    );
  }

  const numeric = field.type === "int" || field.type === "float";
  return (
    <div>
      <label className="label" htmlFor={`f_${field.key}`}>
        {label}
      </label>
      <input
        id={`f_${field.key}`}
        type={numeric ? "number" : "text"}
        inputMode={numeric ? "decimal" : undefined}
        step={field.step ?? (field.type === "int" ? 1 : "any")}
        min={field.min ?? undefined}
        max={field.max ?? undefined}
        className="input w-full"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={field.required !== false}
      />
      {field.description && (
        <div className="mt-1 text-xs text-slate-500">{field.description}</div>
      )}
      {error && <div className="mt-1 text-xs text-rose-600">{error}</div>}
    </div>
  );
}
